import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import '../App.css';
import { getApiKeyHeaders } from '../utils/auth';
import { getBackendUrl } from '../utils/api';

interface EndpointInfo {
  method: string;
  path: string;
  scope: string;
  description: string;
  requestBody?: string;
  responseExample?: string;
  curlExample?: string;
}

interface EndpointGroup {
  title: string;
  description: string;
  endpoints: EndpointInfo[];
}

const CLIENT_API_ENDPOINTS: EndpointGroup[] = [
  {
    title: 'Authentication',
    description: 'OAuth2 Client Credentials flow for API access',
    endpoints: [
      {
        method: 'POST',
        path: '/api/auth/oauth/token',
        scope: 'Public',
        description: 'Exchange client credentials for an access token',
        requestBody: JSON.stringify({
          grant_type: 'client_credentials',
          client_id: 'ps_cl_your_client_id',
          client_secret: 'ps_sec_your_client_secret',
          scope: 'search:text search:image catalog:read'
        }, null, 2),
        responseExample: JSON.stringify({
          access_token: 'eyJhbGciOiJIUzI1NiIs...',
          token_type: 'Bearer',
          expires_in: 3600,
          scope: 'search:text search:image catalog:read'
        }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/auth/oauth/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=client_credentials" \\
  -d "client_id=YOUR_CLIENT_ID" \\
  -d "client_secret=YOUR_CLIENT_SECRET" \\
  -d "scope=search:text search:image"`
      }
    ]
  },
  {
    title: 'Search APIs',
    description: 'Text, image, and hybrid search for automotive parts',
    endpoints: [
      {
        method: 'POST',
        path: '/api/search/text',
        scope: 'search:text',
        description: 'Search for parts using natural language query with NLP extraction',
        requestBody: JSON.stringify({ query: 'brake pads ford ecosport 2019', limit: 10 }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          data: {
            query: 'brake pads ford ecosport 2019',
            results: [{ partNumber: 'FMSI-D1033', partName: 'Front Brake Pad Set', make: 'Ford', model: 'EcoSport' }],
            total: 15,
            responseTime: 450,
            normalizedQuery: { make: 'FORD', model: 'ECOSPORT', partName: 'BRAKE PAD' },
            fromCache: false
          }
        }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/search/text \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "brake pads ford ecosport 2019", "limit": 10}'`
      },
      {
        method: 'POST',
        path: '/api/search/validate',
        scope: 'search:text',
        description: 'Validate query for missing mandatory fields before searching',
        requestBody: JSON.stringify({ query: 'brake pads', source: 'tvs' }, null, 2),
        responseExample: JSON.stringify({
          success: true,
          data: { isValid: false, missingFields: ['Make', 'Model'], normalizedQuery: { partName: 'BRAKE PAD' } }
        }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/search/validate \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "brake pads", "source": "tvs"}'`
      },
      {
        method: 'POST',
        path: '/api/v1/search',
        scope: 'search:text',
        description: 'Structured search using individual MMVFY fields',
        requestBody: JSON.stringify({ make: 'FORD', model: 'ECOSPORT', partName: 'BRAKE PAD', variant: 'TITANIUM', fuelType: 'DIESEL', year: '2019', limit: 10 }, null, 2),
        responseExample: JSON.stringify({ success: true, data: { results: [], total: 0, responseTime: 380 } }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/v1/search \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"make":"FORD","model":"ECOSPORT","partName":"BRAKE PAD","limit":10}'`
      },
      {
        method: 'POST',
        path: '/api/search/image',
        scope: 'search:image',
        description: 'Search for parts using a previously uploaded image',
        requestBody: JSON.stringify({ image_id: 'img_abc123', limit: 10 }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/search/image \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"image_id": "img_abc123", "limit": 10}'`
      },
      {
        method: 'POST',
        path: '/api/search/hybrid',
        scope: 'search:hybrid',
        description: 'Combined text and image search for better accuracy',
        requestBody: JSON.stringify({ query: 'brake pad', image_id: 'img_abc123', limit: 10 }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/search/hybrid \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"query":"brake pad","image_id":"img_abc123","limit":10}'`
      },
      {
        method: 'GET',
        path: '/api/search/suggestions?query={text}',
        scope: 'suggestions:read',
        description: 'Get autocomplete suggestions as the user types',
        curlExample: `curl "{BASE_URL}/api/search/suggestions?query=brake" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      }
    ]
  },
  {
    title: 'Vehicle Data APIs',
    description: 'Vehicle make, model, variant, fuel type, and year data for UI dropdowns',
    endpoints: [
      {
        method: 'GET',
        path: '/api/v1/vehicles/makes',
        scope: 'catalog:read',
        description: 'Get all available vehicle manufacturers',
        responseExample: JSON.stringify({ success: true, data: ['FORD', 'HYUNDAI', 'MARUTI SUZUKI', 'TATA'] }, null, 2),
        curlExample: `curl "{BASE_URL}/api/v1/vehicles/makes" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      },
      {
        method: 'GET',
        path: '/api/v1/vehicles/models?make={make}',
        scope: 'catalog:read',
        description: 'Get models for a specific vehicle make',
        responseExample: JSON.stringify({ success: true, data: ['ECOSPORT', 'ENDEAVOUR', 'FIGO'] }, null, 2),
        curlExample: `curl "{BASE_URL}/api/v1/vehicles/models?make=FORD" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      },
      {
        method: 'GET',
        path: '/api/v1/vehicles/variants?make={make}&model={model}',
        scope: 'catalog:read',
        description: 'Get variants for a specific make and model',
        curlExample: `curl "{BASE_URL}/api/v1/vehicles/variants?make=FORD&model=ECOSPORT" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      },
      {
        method: 'GET',
        path: '/api/v1/vehicles/fuel-types?make={make}&model={model}',
        scope: 'catalog:read',
        description: 'Get fuel types for a specific make and model',
        curlExample: `curl "{BASE_URL}/api/v1/vehicles/fuel-types?make=FORD&model=ECOSPORT" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      },
      {
        method: 'GET',
        path: '/api/v1/vehicles/years?make={make}&model={model}',
        scope: 'catalog:read',
        description: 'Get available years for a vehicle configuration',
        curlExample: `curl "{BASE_URL}/api/v1/vehicles/years?make=FORD&model=ECOSPORT" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      }
    ]
  },
  {
    title: 'Image Upload API',
    description: 'Upload and analyze automotive part images',
    endpoints: [
      {
        method: 'POST',
        path: '/api/upload/image',
        scope: 'search:image',
        description: 'Upload an image for part identification and analysis',
        curlExample: `curl -X POST {BASE_URL}/api/upload/image \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -F "file=@part_photo.jpg"`
      },
      {
        method: 'GET',
        path: '/api/upload/status/{image_id}',
        scope: 'search:image',
        description: 'Check the processing status of an uploaded image',
        curlExample: `curl "{BASE_URL}/api/upload/status/img_abc123" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      },
      {
        method: 'GET',
        path: '/api/upload/{image_id}',
        scope: 'search:image',
        description: 'Get full image data and analysis metadata',
        curlExample: `curl "{BASE_URL}/api/upload/img_abc123" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      },
      {
        method: 'DELETE',
        path: '/api/upload/{image_id}',
        scope: 'search:image',
        description: 'Delete an uploaded image',
        curlExample: `curl -X DELETE "{BASE_URL}/api/upload/img_abc123" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      }
    ]
  },
  {
    title: 'Autocomplete API (A9)',
    description: 'Fast search suggestions using Trie + Vector hybrid engine',
    endpoints: [
      {
        method: 'GET',
        path: '/api/a9/suggestions?q={text}&limit={n}',
        scope: 'suggestions:read',
        description: 'Get A9 autocomplete suggestions with popularity ranking',
        responseExample: JSON.stringify({
          success: true,
          data: { suggestions: ['brake pad maruti swift', 'brake disc hyundai i20'], source: 'hybrid' }
        }, null, 2),
        curlExample: `curl "{BASE_URL}/api/a9/suggestions?q=brake&limit=5" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      },
      {
        method: 'GET',
        path: '/api/a9/popularity?suggestion={text}',
        scope: 'suggestions:read',
        description: 'Get popularity score for a specific suggestion',
        curlExample: `curl "{BASE_URL}/api/a9/popularity?suggestion=brake+pad" \\
  -H "Authorization: Bearer YOUR_TOKEN"`
      }
    ]
  },
  {
    title: 'Voice Search API',
    description: 'Audio transcription for voice-based part search',
    endpoints: [
      {
        method: 'POST',
        path: '/api/asr/transcribe',
        scope: 'search:asr',
        description: 'Transcribe audio file to text for voice search',
        responseExample: JSON.stringify({
          success: true,
          data: { transcription: 'brake pads for ford ecosport 2019', language: 'en', duration: 3.5 }
        }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/asr/transcribe \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -F "file=@voice_search.wav"`
      }
    ]
  },
  {
    title: 'Feedback API',
    description: 'Submit feedback on search result quality',
    endpoints: [
      {
        method: 'POST',
        path: '/api/feedback/submit',
        scope: 'search:text',
        description: 'Submit feedback with rating and optional comment',
        requestBody: JSON.stringify({ rating: 4, comment: 'Found the right part', feedback_type: 'search_quality' }, null, 2),
        responseExample: JSON.stringify({ success: true, feedback_id: 'fb_xyz789' }, null, 2),
        curlExample: `curl -X POST {BASE_URL}/api/feedback/submit \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"rating":4,"comment":"Found the right part","feedback_type":"search_quality"}'`
      }
    ]
  }
];

const SCOPES_TABLE = [
  { scope: 'search:text', description: 'Text-based part search' },
  { scope: 'search:image', description: 'Image-based part search' },
  { scope: 'search:hybrid', description: 'Combined text + image search' },
  { scope: 'search:asr', description: 'Voice/audio search' },
  { scope: 'catalog:read', description: 'Read vehicle and catalog data' },
  { scope: 'suggestions:read', description: 'Autocomplete suggestions' },
  { scope: 'analytics:read', description: 'View analytics data' },
  { scope: 'analytics:write', description: 'Write analytics events' },
  { scope: 'vector:read', description: 'Vector similarity search' },
];

const RATE_LIMITS = [
  { endpoint: 'OAuth2 Token', perMinute: 60, perHour: 600 },
  { endpoint: 'Search APIs', perMinute: 300, perHour: 10000 },
  { endpoint: 'Vehicle Data', perMinute: 300, perHour: 10000 },
  { endpoint: 'Image Upload', perMinute: 50, perHour: 500 },
  { endpoint: 'Autocomplete (A9)', perMinute: 300, perHour: 10000 },
  { endpoint: 'Feedback', perMinute: 200, perHour: 5000 },
];

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: 'bg-green-100 text-green-800',
    POST: 'bg-blue-100 text-blue-800',
    PUT: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded ${colors[method] || 'bg-gray-100 text-gray-800'}`}>
      {method}
    </span>
  );
}

function CodeBlock({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  return (
    <div className="relative mt-2">
      {label && <div className="text-xs text-gray-500 mb-1">{label}</div>}
      <div className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap">
        {code}
      </div>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function EndpointCard({ endpoint }: { endpoint: EndpointInfo }) {
  const [expanded, setExpanded] = useState(false);
  const baseUrl = getBackendUrl();

  return (
    <div className="border border-gray-200 rounded-lg mb-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors"
      >
        <MethodBadge method={endpoint.method} />
        <code className="text-sm font-mono text-gray-800 flex-1">{endpoint.path}</code>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{endpoint.scope}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 p-4 space-y-3">
          <p className="text-sm text-gray-700">{endpoint.description}</p>
          {endpoint.requestBody && (
            <CodeBlock code={endpoint.requestBody} label="Request Body" />
          )}
          {endpoint.responseExample && (
            <CodeBlock code={endpoint.responseExample} label="Response Example" />
          )}
          {endpoint.curlExample && (
            <CodeBlock code={endpoint.curlExample.replace(/\{BASE_URL\}/g, baseUrl)} label="cURL Example" />
          )}
        </div>
      )}
    </div>
  );
}

function ClientApiReference() {
  const [activeSection, setActiveSection] = useState(0);

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <nav className="w-56 flex-shrink-0 hidden lg:block">
        <div className="sticky top-24 space-y-1">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Endpoints</h3>
          {CLIENT_API_ENDPOINTS.map((group, idx) => (
            <button
              key={idx}
              onClick={() => setActiveSection(idx)}
              className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                activeSection === idx ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {group.title}
            </button>
          ))}
          <hr className="my-3 border-gray-200" />
          <button
            onClick={() => setActiveSection(-1)}
            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
              activeSection === -1 ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Scopes Reference
          </button>
          <button
            onClick={() => setActiveSection(-2)}
            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
              activeSection === -2 ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Rate Limits
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Quick Start */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-700 rounded-xl p-6 mb-6 text-white">
          <h2 className="text-lg font-semibold mb-2">Quick Start</h2>
          <p className="text-gray-300 text-sm mb-4">
            1. Get your <code className="bg-gray-800 px-1 rounded">client_id</code> and <code className="bg-gray-800 px-1 rounded">client_secret</code> from your admin&nbsp;&nbsp;
            2. Request an access token via OAuth2&nbsp;&nbsp;
            3. Use the Bearer token in API calls
          </p>
          <div className="bg-gray-800 rounded-lg p-3 text-xs font-mono overflow-x-auto">
            <span className="text-green-400"># Get token</span>{'\n'}
            curl -X POST {getBackendUrl()}/api/auth/oauth/token \{'\n'}
            {'  '}-d "grant_type=client_credentials&client_id=ID&client_secret=SECRET"{'\n\n'}
            <span className="text-green-400"># Search</span>{'\n'}
            curl -X POST {getBackendUrl()}/api/search/text \{'\n'}
            {'  '}-H "Authorization: Bearer TOKEN" \{'\n'}
            {'  '}-H "Content-Type: application/json" \{'\n'}
            {"  -d '{\"query\":\"brake pads ford ecosport\"}'"}
          </div>
        </div>

        {/* Endpoint Groups */}
        {activeSection >= 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">{CLIENT_API_ENDPOINTS[activeSection].title}</h2>
            <p className="text-sm text-gray-600 mb-4">{CLIENT_API_ENDPOINTS[activeSection].description}</p>
            {CLIENT_API_ENDPOINTS[activeSection].endpoints.map((ep, idx) => (
              <EndpointCard key={idx} endpoint={ep} />
            ))}
          </div>
        )}

        {/* Scopes Reference */}
        {activeSection === -1 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Scopes</h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Scope</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {SCOPES_TABLE.map((s, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 font-mono text-xs">{s.scope}</td>
                      <td className="px-4 py-3 text-gray-600">{s.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Rate Limits */}
        {activeSection === -2 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Rate Limits</h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Endpoint Group</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Per Minute</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Per Hour</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {RATE_LIMITS.map((r, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3">{r.endpoint}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.perMinute}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.perHour}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              When rate limited, the response includes <code className="bg-yellow-100 px-1 rounded">X-RateLimit-Remaining</code> and <code className="bg-yellow-100 px-1 rounded">Retry-After</code> headers. Implement exponential backoff in your client.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ApiDocsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'reference' | 'swagger'>('reference');
  const [selectedService, setSelectedService] = useState<string>('search');
  const [openApiSpec, setOpenApiSpec] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const services = [
    { id: 'search', name: 'Search Service', url: '/docs/search-service/openapi.json', port: '30002' },
    { id: 'image-processing', name: 'Image Processing Service', url: '/docs/image-processing/openapi.json', port: '30005' },
    { id: 'tenant-management', name: 'Tenant Management Service', url: '/docs/tenant-management/openapi.json', port: '30001' },
    { id: 'analytics', name: 'Analytics Service', url: '/docs/analytics/openapi.json', port: '30006' },
    { id: 'external-catalog', name: 'External Catalog Service', url: '/docs/external-catalog/openapi.json', port: '30004' },
    { id: 'vector-search', name: 'Vector Search Service', url: '/docs/vector-search/openapi.json', port: '30003' },
  ];

  useEffect(() => {
    if (activeTab === 'swagger') {
      loadOpenApiSpec();
    }
  }, [selectedService, activeTab]);

  const loadOpenApiSpec = async () => {
    setLoading(true);
    setError('');

    try {
      const service = services.find(s => s.id === selectedService);
      if (!service) {
        setError('Service not found');
        setLoading(false);
        return;
      }

      const gatewayUrl = getBackendUrl();
      const specUrl = `${gatewayUrl}${service.url}`;

      try {
        const headers: HeadersInit = getApiKeyHeaders();
        const response = await fetch(specUrl, { headers });
        if (!response.ok) {
          throw new Error(`Failed to load OpenAPI spec: ${response.statusText} (${response.status})`);
        }
        const spec = await response.json();
        setOpenApiSpec(spec);
      } catch (fetchError: any) {
        setError(`Failed to load API documentation: ${fetchError.message}. Ensure the API Gateway is running and the route is configured in Kong.`);
      }
    } catch (err: any) {
      setError(`Error loading API documentation: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="Back to home"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">API Documentation</h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Tab Switcher */}
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setActiveTab('reference')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'reference' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Client Reference
                </button>
                <button
                  onClick={() => setActiveTab('swagger')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'swagger' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Swagger UI
                </button>
              </div>

              {/* Service Selector (only for Swagger tab) */}
              {activeTab === 'swagger' && (
                <div className="flex items-center gap-2">
                  <label htmlFor="service-select" className="text-sm font-medium text-gray-700">
                    Service:
                  </label>
                  <select
                    id="service-select"
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Client Reference Tab */}
        {activeTab === 'reference' && <ClientApiReference />}

        {/* Swagger UI Tab */}
        {activeTab === 'swagger' && (
          <>
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                <span className="ml-4 text-gray-600">Loading API documentation...</span>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && openApiSpec && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <SwaggerUI
                  spec={openApiSpec}
                  tryItOutEnabled={true}
                  requestInterceptor={(request: any) => {
                    const headers = getApiKeyHeaders() as Record<string, string>;
                    if (headers['Authorization']) {
                      request.headers['Authorization'] = headers['Authorization'];
                    }
                    return request;
                  }}
                />
              </div>
            )}

            {!loading && !error && !openApiSpec && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-600">No API documentation available for this service.</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default ApiDocsPage;
